import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { actions } from '@ecosystem/newron-design-system';
import ActionBar from '@components/action-bar/ActionBar';
import { useCategoryOrSegmentsLeaderboardGroupEventState } from '../utils/CategoryOrSegmentsLeaderboardGroupContext';
import {
  useCategoryLeaderboardGroupState,
  useFeatureLeaderboardGroupState,
} from '@utils/hook/userPreferences/UseGroupState';
import {
  EXPLORE_LEADERBOARD_TABLE,
  GROUP_PGS_LIMIT_MESSAGE,
  GroupContext,
  PreferenceResponseStatus,
  PRODUCT_GROUP_MAX_SELECTION,
  totalNumberSelectedForAddingItems,
  totalNumberSelectedForItems,
} from '@utils/userPreferencesUtils/Types';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import {
  CATEGORY_GROUP_TABLE_ELEMENT_CLICKED_TITLE,
  groupElementClickedTrackingFunction,
  TableElementClickedParam,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import { FeatureOption, PageName, PageType } from '@utils/pageConfig/Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { CATEGORY_FEATURE_OPTION } from '@utils/hook/selector/ShowPerformanceWithinSelector';

const { Button } = actions;

interface Props {
  totalProductGroups: number;
  context: GroupContext;
  isEditable: boolean;
  setIsEditable: Dispatch<SetStateAction<boolean>>;
  isLimitPG: boolean;
}

/**
 * @return ActionBar
 *
 * @remarks
 * to display a grey bar on the bottom of the Category Leaderboard page with to display some message and buttons
 * for grouping
 *
 * @param totalProductGroups - total number of product groups
 * @param context - contains retailer client and country
 * @param isEditable - local state to check if its editable
 * @param setIsEditable - set state for isEditable
 * @param isLimitPG - state to display LimitInfo Banner instead of Category Table
 * **/

export const CategoryLeaderboardActionBar: React.FC<Props> = ({
  totalProductGroups,
  context,
  isEditable,
  setIsEditable,
  isLimitPG,
}) => {
  const {
    selectedProductGroupOrSegments,
    setIsCreateModalGroupPopUp,
    isEditGroupMode,
    setIsEditGroupMode,
    clearSelectedPgsOrSegments,
    currentSelectedFeature,
  } = useCategoryOrSegmentsLeaderboardGroupEventState();
  const { updateCategoryLeaderboardGroup } = useCategoryLeaderboardGroupState(context);
  const { updateFeatureLeaderboardGroup } = useFeatureLeaderboardGroupState(context);
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);

  const noPgsSelected: boolean = selectedProductGroupOrSegments.length === 0;
  const atLeastOnePgSelected: boolean = selectedProductGroupOrSegments.length >= 1;
  const isGroupPgsExceeded: boolean = selectedProductGroupOrSegments.length > PRODUCT_GROUP_MAX_SELECTION;

  const totalSelectedProductGroupsInEditMode =
    selectedProductGroupOrSegments.length + isEditGroupMode.groupDetails.segmentOrPgValue.length;

  const isGroupPgsExceededInEditMode: boolean = totalSelectedProductGroupsInEditMode > PRODUCT_GROUP_MAX_SELECTION;

  const currentFeature: FeatureOption =
    pagesConfig.pageType === PageType.CATEGORY_LEADERBOARD ? CATEGORY_FEATURE_OPTION : currentSelectedFeature;

  useEffect(() => {
    //clear selected pgs or segments when limit info banner is shown
    if (isLimitPG) {
      clearSelectedPgsOrSegments();
    }
  }, [isLimitPG]);

  const handleCancelEditMode = () => {
    setIsEditGroupMode({
      groupDetails: { groupName: '', segmentOrPgValue: [], sortOrder: undefined },
      isEditMode: false,
    });
  };

  if (isEditable) {
    setIsEditGroupMode({
      groupDetails: { groupName: '', segmentOrPgValue: [], sortOrder: undefined },
      isEditMode: false,
    });
    setIsEditable(false);
    clearSelectedPgsOrSegments();
  }

  const constructUpdateCategoryLeaderboardGroupPreference = (updatedProductGroups: string[]) => {
    updateCategoryLeaderboardGroup.mutate(
      {
        key: isEditGroupMode.groupDetails.groupName,
        data: {
          productGroup: updatedProductGroups,
          showPerformanceWithin: currentFeature.feature,
          sortOrder: isEditGroupMode.groupDetails.sortOrder!,
        },
      },
      {
        onSuccess: (response) => {
          if (response.status === PreferenceResponseStatus.SUCCESS) {
            clearSelectedPgsOrSegments();
            handleCancelEditMode();
          }
        },
      },
    );
  };

  const constructUpdateFeatureLeaderboardGroupPreference = (updatedProductGroups: string[]) => {
    updateFeatureLeaderboardGroup.mutate(
      {
        key: isEditGroupMode.groupDetails.groupName,
        data: {
          value: updatedProductGroups,
          showPerformanceWithin: currentFeature.feature,
          sortOrder: isEditGroupMode.groupDetails.sortOrder!,
        },
      },
      {
        onSuccess: (response) => {
          if (response.status === PreferenceResponseStatus.SUCCESS) {
            clearSelectedPgsOrSegments();
            handleCancelEditMode();
          }
        },
      },
    );
  };

  const handleUpdateGroups = () => {
    const updatedProductGroups: string[] = [
      ...isEditGroupMode.groupDetails.segmentOrPgValue,
      ...selectedProductGroupOrSegments,
    ];

    //Amplitude Tracking when Add product group(s) button is click
    const trackingParam: TableElementClickedParam = {
      eventTitle: CATEGORY_GROUP_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: `Add ${currentFeature.feature} group`,
      elementType: `Table ${currentFeature.feature} group`,
      track: track,
      optionChosen: selectedProductGroupOrSegments.flatMap((selectedPg) => selectedPg).join(', '),
      pageName: PageName.EXPLORE,
      pageType: PageType.EXPLORE,
      sideFilters: sideFiltersState.appliedFilters,
      access: pagesConfig.access,
      tableName: EXPLORE_LEADERBOARD_TABLE,
    };

    groupElementClickedTrackingFunction(trackingParam);

    currentFeature === null || currentFeature.feature === 'Category'
      ? constructUpdateCategoryLeaderboardGroupPreference(updatedProductGroups)
      : constructUpdateFeatureLeaderboardGroupPreference(updatedProductGroups);
  };

  if (isEditGroupMode.isEditMode) {
    return (
      <ActionBar
        isHidden={!isEditGroupMode.isEditMode}
        leftMessage={totalNumberSelectedForAddingItems(
          isEditGroupMode.groupDetails.groupName,
          totalSelectedProductGroupsInEditMode,
          PRODUCT_GROUP_MAX_SELECTION,
        )}
        centerMessage={isGroupPgsExceededInEditMode ? GROUP_PGS_LIMIT_MESSAGE(currentFeature.feature) : ''}
      >
        {atLeastOnePgSelected && (
          <Button
            testId="cl-action-bar-add-to-group"
            disabled={isGroupPgsExceededInEditMode}
            onClick={handleUpdateGroups}
          >
            Add to group
          </Button>
        )}
        <Button testId="cl-action-bar-cancel" mode="secondary" onClick={handleCancelEditMode}>
          Cancel
        </Button>
      </ActionBar>
    );
  }

  return (
    <ActionBar
      leftMessage={totalNumberSelectedForItems(
        selectedProductGroupOrSegments.length,
        totalProductGroups,
        currentFeature.feature,
      )}
      centerMessage={isGroupPgsExceeded ? GROUP_PGS_LIMIT_MESSAGE(currentFeature.feature) : ''}
      rightMessage={noPgsSelected ? GROUP_PGS_LIMIT_MESSAGE(currentFeature.feature) : ''}
    >
      {atLeastOnePgSelected && (
        <Button
          testId="cl-action-bar-group"
          disabled={isGroupPgsExceeded}
          onClick={() => setIsCreateModalGroupPopUp(true)}
        >
          Group
        </Button>
      )}
    </ActionBar>
  );
};
